import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import WrongLocationIcon from '@mui/icons-material/WrongLocation';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridRowOrderChangeParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  Reservation,
  TripLegType,
  TripLeg,
  TripLegAddress,
  OnHoldTripLegApproveDenyAction,
} from '../../../data/models/reservation.model';
import { DateTime } from 'luxon';
import StatusChip from './StatusChip';
import * as reservationSlice from '../../../data/reservation.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import TripLegDialog from '../reservations/TripLegDialog';
import { Passenger } from '../../../data/models/passenger.model';
import { ServicePlan } from '../../../data/models/service-plan.model';
import reservationUtils from '../../../services/reservationUtils';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CancelTripLegDialog from './CancelTripLegDialog';
import CancelTripLegUnavailableDialog from './CancelTripLegUnavailableDialog';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Link, useNavigate } from 'react-router-dom';
import dateUtils from '../../../services/DateUtils';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { usePortalName } from '../../../hooks/usePortalName';

const PREFIX = 'ReservationTable';

const classes = {
  container: `${PREFIX}-container`,
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  grid: `${PREFIX}-grid`,
  address: `${PREFIX}-address`,
  line2: `${PREFIX}-line2`,
  hasRoundTripIcon: `${PREFIX}-hasRoundTripIcon`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    marginTop: theme.spacing(1),
    boxShadow: 'none',
  },

  [`& .${classes.panelTitle}`]: {
    fontWeight: '600',
    fontSize: 14,
    letterSpacing: 0.17,
    margin: 'auto 0',
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },

  [`& .${classes.address}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.line2}`]: {
    fontWeight: 400,
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.hasRoundTripIcon}`]: {
    marginLeft: theme.spacing(3),
  },
}));

interface ReservationTableProps {
  passenger: Passenger | null;
  loadingPassenger: boolean;
  reservations: { reservations: Reservation[]; continuationToken?: string };
  servicePlans: ServicePlan[];
}

export default function ReservationTable({
  passenger,
  loadingPassenger,
  reservations,
  servicePlans,
}: ReservationTableProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isPortal = usePortalName() ? true : false;

  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();

  const isSavingTrip = useAppSelector(reservationSlice.selectSavingTripLeg);
  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const [editOpen, setEditOpen] = React.useState(false);
  const [
    cancelTripLegUnavailableDialogOpen,
    setCancelTripLegUnavailableDialogOpen,
  ] = React.useState(false);
  const [tripLeg, setTripLeg] = React.useState<TripLeg>();
  const [tripType, setTripType] = React.useState<TripLegType>(
    TripLegType.MultiLeg,
  );
  const [editReservationReferenceID, setEditReservationReferenceID] =
    React.useState('');
  const [editTripLegSequence, setEditTripLegSequence] = React.useState(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmTitle, setConfirmTitle] = React.useState('');
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [confirmReservation, setConfirmReservation] = React.useState(true);
  const [cancelPassengerUUID, setCancelPassengerUUID] = React.useState('');
  const [cancelReservationId, setCancelReservationId] = React.useState('');
  const [cancelTripId, setCancelTripId] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const stoMenuOpean = Boolean(anchorEl);
  const [cancelStandingOrderUUID, setCancelStandingOrderUUID] =
    React.useState<string>();
  const [
    cancelAllStandingOrderReservation,
    setCancelAllStandingOrderReservations,
  ] = React.useState(false);
  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);
  const loadingSyncedTripLegs = useAppSelector(
    tripLegSlice.selectLoadingTripLegDetails,
  );

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    if (row.isWillCall) {
      return (
        <Typography textTransform="uppercase">
          {dateUtils.formatAppointmentDate(
            params.row.appointmentDate,
            params.row.dropOffAddress.timeZone,
          ) +
            ' ' +
            t('reservation.willCallTime')}
        </Typography>
      );
    } else {
      return (
        <Typography>
          {dateUtils.formatPickupDateTime(
            params.row.pickUpDueDateTime,
            params.row.pickUpAddress.timeZone,
          )}
        </Typography>
      );
    }
  };

  const renderAddress = (address: TripLegAddress) => {
    return (
      <Box className={classes.address}>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography
          className={
            classes.line2
          }>{`${address.city}, ${address.stateCode} ${address.zipCode}`}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    return renderAddress(params.row.pickUpAddress);
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    return renderAddress(params.row.dropOffAddress);
  };

  const renderRoundTrip = (params: GridCellParams): React.ReactElement => {
    const isRoundTrip = !!params.row.isRoundTrip;
    const status = isRoundTrip ? t('common.yes') : t('common.no');
    return <StatusChip status={status} />;
  };

  const handleApproveOrDenyLeg = (
    action: OnHoldTripLegApproveDenyAction,
    params: GridCellParams,
  ) => {
    const data = reservationUtils.getApproveOrDenyTripLegsFromData(
      params.row.tripLegReferenceID,
      action,
      params.row.reservation,
    );
    dispatch(
      reservationSlice.approveOrDenyTripLegsAsync(
        data.passengerUUID,
        data.reservationReferenceID,
        data.tripLegReferenceIDs,
        data.action,
      ),
    );
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    return (
      <StatusChip
        status={params.row.status}
        onApproveOrDeny={(action: OnHoldTripLegApproveDenyAction) =>
          handleApproveOrDenyLeg(action, params)
        }
      />
    );
  };

  const onAddTripLeg = (evt: any, resid: string, seq: number) => {
    evt.stopPropagation();
    evt.preventDefault();
    setEditReservationReferenceID(resid);
    setEditTripLegSequence(seq);
    setTripType(TripLegType.MultiLeg);
    setTripLeg(undefined);
    setEditOpen(true);
  };

  // Pending cancel state - handles both reservation and trip leg cancellation
  const [pendingCancel, setPendingCancel] = React.useState<{
    type: 'reservation' | 'tripLeg';
    tripLegReferenceId: string;
    reservationId: string;
  } | null>(null);

  // Effect to check editability after trip leg details are loaded
  React.useEffect(() => {
    if (pendingCancel && !loadingSyncedTripLegs) {
      // Cancel and Edit are using same logic
      const canEditTrip = reservationUtils.canPoralUserEditTripLeg(
        syncedTripLegs[pendingCancel.tripLegReferenceId],
        isPortal,
      );
      if (canEditTrip) {
        const currentPending = pendingCancel;
        setPendingCancel(null);

        // Common acrosss reservation and tripleg.
        setCancelPassengerUUID(passenger!.passengerUUID!);
        setCancelReservationId(currentPending.reservationId);

        if (currentPending.type === 'reservation') {
          setConfirmReservation(true);
          setConfirmTitle(t('passenger.cancelReservation'));
          setConfirmMessage(t('passenger.cancelReservationMessage'));
        } else {
          setCancelTripId(currentPending.tripLegReferenceId);
          setConfirmReservation(false);
          setConfirmTitle(t('passenger.cancelTripLeg'));
          setConfirmMessage(t('passenger.cancelTripLegMessage'));
        }
        setConfirmOpen(true);
      } else {
        // Show Cancel Trip Dialog
        setCancelTripLegUnavailableDialogOpen(true);
      }
    }
  }, [pendingCancel, loadingSyncedTripLegs, syncedTripLegs]);

  const cancelReservation = (id: string) => {
    const reservation = reservations.reservations.find(
      (x) => x.reservationReferenceID == id,
    );
    if (reservation && reservation.tripLegs.length > 0) {
      const tripLeg = reservation?.tripLegs.at(0);
      // Dispatch the API call and set pending state to wait for response
      dispatch(
        tripLegSlice.getTripLegDetailsAsync(tripLeg?.tripLegReferenceID!),
      );
      setPendingCancel({
        type: 'reservation',
        tripLegReferenceId: tripLeg?.tripLegReferenceID!,
        reservationId: id,
      });
    }
  };

  const cancelSTOReservation = (all: boolean) => {
    setCancelAllStandingOrderReservations(all);
    cancelReservation(cancelReservationId);
  };

  const onCancelReservation = (
    evt: any,
    id: string,
    standingOrderUUID?: string,
  ) => {
    evt.stopPropagation();
    evt.preventDefault();
    if (standingOrderUUID) {
      setAnchorEl(evt.currentTarget);
      setCancelReservationId(id);
      setCancelStandingOrderUUID(standingOrderUUID);
    } else {
      cancelReservation(id);
    }
  };

  const editTripLeg = (leg: TripLeg) => {
    const isRoundTrip = !!leg.isRoundTrip;
    const pid = passenger!.passengerUUID!;
    const rid = leg.reservationReferenceID!;
    const lid = leg.tripLegReferenceID!;
    setEditReservationReferenceID(rid);
    setEditTripLegSequence(leg.tripLegSequence);
    dispatch(reservationSlice.getTripLegAsync(pid, rid, lid));
    setTripType(isRoundTrip ? TripLegType.RoundTrip : TripLegType.MultiLeg);
    setTripLeg(leg);
    setEditOpen(true);
  };

  const cancelTripLeg = (leg: TripLeg) => {
    // Dispatch the API call and set pending state to wait for response
    dispatch(tripLegSlice.getTripLegDetailsAsync(leg.tripLegReferenceID!));
    setPendingCancel({
      type: 'tripLeg',
      tripLegReferenceId: leg.tripLegReferenceID!,
      reservationId: leg.reservationReferenceID!,
    });
  };

  const onCancelTripLeg = (leg: TripLeg) => {
    cancelTripLeg(leg);
  };

  const handleSTOCancelClose = () => {
    setAnchorEl(null);
  };

  const activateWillCall = (leg: TripLeg) => {
    const tz = leg.pickUpAddress.timeZone;
    const now = DateTime.local().plus({ minutes: 1 }).setZone(tz);
    const l = reservationUtils.extractTripLegFields(leg);
    const pickUpDueDateTime = now.toUTC().toISO()!;
    const appointmentDateTime = now.plus({ minutes: 1 }).toUTC().toISO()!;
    const updatedLeg: TripLeg = {
      ...l,
      pickUpDueDateTime,
      isWillCall: false,
      appointmentDateTime,
    };
    dispatch(reservationSlice.updateTripLegsAsync([updatedLeg], false));
  };

  const handleRowOrderChange = (
    params: GridRowOrderChangeParams,
    reservation: Reservation,
  ) => {
    const legs = reservationUtils.reorderTripLegs(
      reservation.tripLegs,
      params.oldIndex,
      params.targetIndex,
    );
    dispatch(
      reservationSlice.reorderTripLegsAsync(
        passenger!.passengerUUID!,
        reservation.reservationReferenceID!,
        legs,
      ),
    );
  };

  const handleConfirmationClose = (data: any) => {
    const tripLegStatusReasonBCs: string[] = [];
    if (data) {
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          tripLegStatusReasonBCs.push(data[key]);
        }
      });

      if (confirmReservation) {
        if (cancelAllStandingOrderReservation && cancelStandingOrderUUID) {
          dispatch(
            reservationSlice.cancelStandingOrderReservationAsync(
              cancelStandingOrderUUID,
              tripLegStatusReasonBCs,
            ),
          );
        } else {
          dispatch(
            reservationSlice.cancelReservationAsync(
              cancelPassengerUUID,
              cancelReservationId,
              tripLegStatusReasonBCs,
            ),
          );
        }
      } else {
        dispatch(
          reservationSlice.cancelTripLegAsync(
            cancelPassengerUUID,
            cancelReservationId,
            cancelTripId,
            tripLegStatusReasonBCs,
          ),
        );
      }
    }
    setCancelStandingOrderUUID(undefined);
    setCancelReservationId('');
    setConfirmOpen(false);
  };

  const handleTripModeChange = (res: Reservation) => {
    const saveAsDraft = res.tripLegs[0].state === 'draft';
    dispatch(
      reservationSlice.updateTripLegsAsync(
        res.tripLegs.map((l) => {
          const newLeg = reservationUtils.extractTripLegFields(l);
          return {
            ...newLeg,
            metadata: {
              ...(l.metadata || {}),
              roundTrip: false,
              roundTripReturnLeg: false,
            },
          };
        }),
        saveAsDraft,
      ),
    );
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate(
      `/passengers/${passenger?.passengerUUID}/reservations/${params.row.reservationReferenceID}?tripLegID=${params.row.tripLegReferenceID}`,
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'tripLegReferenceID',
      headerName: t('passenger.tripId'),
      width: 80,
      sortable: false,
    },
    {
      field: 'date',
      headerName: t('passenger.dateTime'),
      width: 180,
      sortable: false,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('passenger.pickupAddress'),
      flex: 1,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('passenger.dropoffAddress'),
      flex: 1,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'tripStatus',
      headerName: t('passenger.status'),
      headerAlign: 'center',
      width: 160,
      sortable: false,
      align: 'center',
      renderCell: renderStatus,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 150,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const returnLeg = params.row.metadata?.roundTripReturnLeg;
        return (
          <>
            {returnLeg && (
              <Tooltip title={t('passenger.roundTrip')}>
                <KeyboardReturnOutlinedIcon />
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip
                title={t('passenger.editTripLeg')}
                className={returnLeg ? '' : classes.hasRoundTripIcon}>
                <span>
                  <IconButton
                    data-testid="edit"
                    disabled={loadingPassenger}
                    onClick={(evt) => {
                      evt.stopPropagation();
                      editTripLeg(params.row);
                    }}
                    size="medium"
                    color="primary">
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser &&
              !params.row.isRoundTrip &&
              !params.row.isSingleLegTrip && (
                <Tooltip title={t('passenger.cancelTripLeg')}>
                  <span>
                    <IconButton
                      data-testid="cancel-leg"
                      data-fs-element="CancelTripIconButton"
                      disabled={
                        loadingPassenger ||
                        !reservationUtils.isTripLegCancelable(params.row)
                      }
                      onClick={(evt) => {
                        evt.stopPropagation();
                        onCancelTripLeg(params.row);
                      }}
                      color="warning"
                      size="medium">
                      <WrongLocationIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            {!isSuperReaderUser && params.row.isWillCall && (
              <Tooltip title={t('passenger.activateWillCall')}>
                <span>
                  <IconButton
                    data-testid="willcall"
                    disabled={
                      loadingPassenger ||
                      !reservationUtils.isTripLegActivatable(params.row)
                    }
                    onClick={(evt) => {
                      evt.stopPropagation();
                      activateWillCall(params.row);
                    }}
                    size="medium"
                    color="primary">
                    <PhoneIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const getPanelTitle = (reservation: Reservation) => {
    const referenceID = (reservation as any).reservationReferenceID;
    const firstTripLeg = reservation.tripLegs[0];

    if (!firstTripLeg?.pickUpAddress) {
      return (
        <Typography component="span" className={classes.panelTitle}>
          {`${t('reservation.reservationId')}: `}
          <Link to={`reservations/${referenceID}`}>{referenceID}</Link>
        </Typography>
      );
    }

    const timeZone = firstTripLeg.pickUpAddress.timeZone;
    const getTimeZoneName = (
      tzCode: string | undefined,
    ): string | undefined => {
      const tz = timezones?.find((z) => z.ianaStandard === tzCode);
      return tz?.timeZoneName;
    };
    const panelTitleTimeZone = getTimeZoneName(timeZone);
    let reservationTime =
      firstTripLeg &&
      dateUtils.formatAppointmentDate(firstTripLeg.appointmentDate, timeZone!);

    return (
      <>
        <Typography component="span" className={classes.panelTitle}>
          {`${t('reservation.reservationId')}: `}
          <Link to={`reservations/${referenceID}`}>{referenceID}</Link>
        </Typography>
        {reservationTime && (
          <Typography
            component="span"
            className={classes.panelTitle}
            textTransform="capitalize">
            {' '}
            - {reservationTime} {panelTitleTimeZone}
          </Typography>
        )}
      </>
    );
  };

  return (
    <StyledPaper className={classes.container}>
      {[...reservations.reservations]
        ?.sort(
          (a, b) =>
            new Date(a.reservationServiceDate || Date.now()).getTime() -
            new Date(b.reservationServiceDate || Date.now()).getTime(),
        )
        ?.map((res) => {
          const sortedTripLegs = [...res.tripLegs].sort((a, b) => {
            const seqDiff = (a.tripLegSequence || 0) - (b.tripLegSequence || 0);
            if (seqDiff !== 0) return seqDiff;
            const aTime = a.pickUpDueDateTime || a.appointmentDateTime || '';
            const bTime = b.pickUpDueDateTime || b.appointmentDateTime || '';
            if (!aTime && !bTime) return 0;
            if (!aTime) return 1;
            if (!bTime) return -1;
            return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
          });
          const isRoundTrip = reservationUtils.isRoundTrip(res);
          const isSingleLegTrip = sortedTripLegs.length === 1;
          const isStandingOrder = !!res.standingOrderUUID;
          const isOpenOrDraft = sortedTripLegs.reduce(
            (status, leg) =>
              status && (leg.status === 'open' || leg.status === 'draft'),
            true,
          );
          let roundTripWillCall = false;
          let roundTripReturnTime = '';
          let roundTripReturnTimeZone = '';
          let roundTripTripLegReferenceID = '';
          if (isRoundTrip) {
            roundTripWillCall = sortedTripLegs[1].isWillCall;
            roundTripReturnTime = sortedTripLegs[1].pickUpDueDateTime || '';
            roundTripReturnTimeZone =
              sortedTripLegs[1].dropOffAddress.timeZone || '';
            roundTripTripLegReferenceID = sortedTripLegs[1].tripLegReferenceID!;
          }

          const getRows = () => {
            return sortedTripLegs.map((l) => ({
              ...l,
              isSingleLegTrip,
              roundTripWillCall,
              roundTripReturnTime,
              roundTripReturnTimeZone,
              roundTripTripLegReferenceID,
              reservationReferenceID: res.reservationReferenceID,
              reservation: res,
            }));
          };

          return (
            <Accordion key={res.reservationReferenceID}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box className={classes.summary}>
                  <Box className={classes.panelTitle}>{getPanelTitle(res)}</Box>
                  <Box>
                    {isStandingOrder && (
                      <Tooltip title={t('reservation.standingOrderLabel')}>
                        <LoopIcon
                          sx={{ verticalAlign: 'middle', cursor: 'default' }}
                        />
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && isRoundTrip && (
                      <Tooltip title={t('passenger.convertToMultiLeg')}>
                        <span>
                          <IconButton
                            data-testid="convert-to-multi-leg"
                            disabled={loadingPassenger || isSavingTrip}
                            onClick={(evt) => {
                              evt.stopPropagation();
                              handleTripModeChange(res);
                            }}
                            color="primary"
                            size="medium">
                            <KeyboardDoubleArrowRightIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isRoundTrip && !isSuperReaderUser && (
                      <Tooltip title={t('passenger.addTripLeg')}>
                        <span>
                          <IconButton
                            data-testid="add-leg"
                            disabled={loadingPassenger || isSavingTrip}
                            onClick={(evt) =>
                              onAddTripLeg(
                                evt,
                                res.reservationReferenceID!,
                                res.tripLegs.length,
                              )
                            }
                            color="primary"
                            size="medium">
                            <PlaceOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip title={t('passenger.cancelReservation')}>
                        <span>
                          <IconButton
                            data-testid="cancel-reservation"
                            data-fs-element="CancelReservationIconButton"
                            disabled={
                              loadingPassenger ||
                              isSavingTrip ||
                              !reservationUtils.isReservationCancelable(res)
                            }
                            onClick={(evt) =>
                              onCancelReservation(
                                evt,
                                res.reservationReferenceID!,
                                res.standingOrderUUID,
                              )
                            }
                            color="error"
                            size="medium">
                            <HighlightOffIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid' }}>
                  <DataGridPro
                    getRowId={(row) => row.tripLegReferenceID}
                    rows={getRows()}
                    columns={columns}
                    rowReordering={
                      res.tripLegs.length > 1 &&
                      !reservationUtils.isRoundTrip(res) &&
                      !isSuperReaderUser
                    }
                    autoHeight={true}
                    rowThreshold={0}
                    rowSelection={false}
                    className={classes.grid}
                    checkboxSelection={false}
                    disableColumnMenu={true}
                    disableColumnSelector={true}
                    disableRowSelectionOnClick={true}
                    hideFooter={true}
                    onRowClick={handleRowClick}
                    sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                    onRowOrderChange={(params) =>
                      handleRowOrderChange(params, res)
                    }
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      {passenger && (
        <TripLegDialog
          sequence={editTripLegSequence}
          reservationReferenceID={editReservationReferenceID}
          tripLeg={tripLeg}
          tripType={tripType}
          open={editOpen}
          passenger={passenger!}
          servicePlans={servicePlans}
          close={() => setEditOpen(false)}
        />
      )}
      <CancelTripLegDialog
        open={confirmOpen}
        close={handleConfirmationClose}
        dialogTitle={confirmTitle}
        dialogMessage={confirmMessage}
        submitDataFsElement={
          confirmReservation
            ? 'CancelReservationSubmitButton'
            : 'CancelTripSubmitButton'
        }
      />
      <CancelTripLegUnavailableDialog
        dialogOpen={cancelTripLegUnavailableDialogOpen || loadingSyncedTripLegs}
        loadingSyncedTripLeg={loadingSyncedTripLegs}
        showUnavailableMessage={cancelTripLegUnavailableDialogOpen}
        onClose={() => setCancelTripLegUnavailableDialogOpen(false)}
      />
      <Menu
        id="cancel-sto"
        anchorEl={anchorEl}
        open={stoMenuOpean}
        onClose={handleSTOCancelClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem
          key={0}
          onClick={() => {
            handleSTOCancelClose();
            cancelSTOReservation(false);
          }}>
          {t('reservation.cancelSTOThis')}
        </MenuItem>
        <MenuItem
          key={1}
          onClick={() => {
            handleSTOCancelClose();
            cancelSTOReservation(true);
          }}>
          {t('reservation.cancelSTOAll')}
        </MenuItem>
      </Menu>
    </StyledPaper>
  );
}
